// Catálogo de ingredientes de pizza: qué forma tiene cada uno y de qué color es.
//
// 🔴 POR QUÉ EXISTE. Antes el color salía de un HASH DEL NOMBRE contra una paleta
// genérica. Es decir: el pepperoni podía tocarle beige y la piña violeta. Ninguna
// cantidad de sombras, texturas ni brillos arregla una pizza cuyos ingredientes
// tienen el color equivocado — es la razón de fondo por la que el dibujo no se veía
// real, por encima de cualquier detalle de dibujo.
//
// Acá el color y la forma salen del INGREDIENTE, no del azar. Es una lista curada de
// lo que de verdad se pone en una pizza; para lo que no está, se cae al hash de antes
// (una pizzería puede tener "Chistorra de la casa" y algo hay que dibujar).
//
// El nombre se normaliza sin tildes porque el dueño escribe su menú a mano:
// "Champiñones", "champinones" y "CHAMPIÑON" tienen que caer en el mismo lugar.

export type Forma =
  | "disco" | "hongo" | "anillo" | "tira" | "cuna" | "jiron"
  | "rizo" | "grano" | "hoja" | "escama" | "huevo"

export type PresetIngrediente = {
  forma: Forma
  /** Color real del ingrediente, no uno asignado por azar. */
  color: string
  /** Cuántas piezas caben en una pizza entera. Las hojas y escamas van muchas y
   *  chicas; una lonja de jamón, pocas y grandes. */
  densidad?: number
  /** Multiplicador de tamaño de pieza. */
  escala?: number
}

/** Normaliza para comparar: minúsculas, sin tildes. */
export function normalizar(nombre: string): string {
  return nombre.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

/**
 * El orden IMPORTA: se devuelve la primera que coincide.
 *
 * Los casos más específicos van primero — "cebolla morada" antes que "cebolla",
 * "aceituna negra" antes que "aceituna". Al revés, la regla general se comería a la
 * específica y la cebolla morada saldría blanca.
 *
 * 🔴 LAS PALABRAS CORTAS VAN ANCLADAS CON `\b`. Sin eso, un ingrediente cae dentro
 * de otro por pura coincidencia de letras y se dibuja mal sin que nadie lo note:
 *   · "acei·TUNA·s"  caía en /tuna/  → las aceitunas se dibujaban como atún;
 *   · "es·PINA·ca"   cae en /pina/   → la espinaca saldría como cuña de piña;
 *   · /res/ vive dentro de decenas de palabras.
 * Los dos primeros estaban pasando de verdad — los cazó el test del catálogo.
 */
const CATALOGO: Array<{ re: RegExp; preset: PresetIngrediente }> = [
  // ── Embutidos y carnes ────────────────────────────────────────────────────
  { re: /pepperoni|peperoni|pepproni/, preset: { forma: "disco", color: "#C6231B", densidad: 14, escala: 1.15 } },
  { re: /salami|salame/,               preset: { forma: "disco", color: "#A63328", densidad: 13, escala: 1.15 } },
  { re: /chorizo/,                     preset: { forma: "disco", color: "#9C3A1C", densidad: 12, escala: 1.1 } },
  { re: /jamon|prosciut|serrano/,      preset: { forma: "jiron", color: "#E39A93", densidad: 9,  escala: 1.35 } },
  { re: /tocin|bacon|panceta/,         preset: { forma: "rizo",  color: "#8E4A30", densidad: 10, escala: 1.2 } },
  { re: /salchich|chistorra|butifarra/,preset: { forma: "grano", color: "#8B5A3C", densidad: 14 } },
  { re: /pollo|pechuga/,               preset: { forma: "grano", color: "#D6B27C", densidad: 15 } },
  { re: /\bcarne\b|\bres\b|molida|beef/, preset: { forma: "grano", color: "#6B4028", densidad: 18, escala: 0.85 } },
  { re: /anchoa|anchov/,               preset: { forma: "tira",  color: "#8A7458", densidad: 8,  escala: 0.9 } },
  { re: /\batun\b|\btuna\b/,            preset: { forma: "grano", color: "#C6A98A", densidad: 16, escala: 0.85 } },

  // ── Vegetales ─────────────────────────────────────────────────────────────
  { re: /champin|hongo|\bseta\b|portobello/, preset: { forma: "hongo", color: "#D8CBB6", densidad: 13, escala: 1.1 } },
  { re: /cebolla mora|cebolla roja/,     preset: { forma: "anillo", color: "#A9739E", densidad: 12, escala: 1.15 } },
  { re: /cebolla|onion/,                 preset: { forma: "anillo", color: "#EFE6EE", densidad: 12, escala: 1.15 } },
  { re: /aceitun.*negra|oliva.*negra/,   preset: { forma: "anillo", color: "#3B3339", densidad: 13 } },
  { re: /aceitun|oliva|olive/,           preset: { forma: "anillo", color: "#8C9C3E", densidad: 13 } },
  { re: /jalapen|\bchile\b/,              preset: { forma: "anillo", color: "#4E9A3F", densidad: 14, escala: 0.9 } },
  { re: /pimen.*roj|morron roj/,         preset: { forma: "tira",  color: "#C33A2C", densidad: 12 } },
  { re: /pimen|piment|morron|\baji\b|bell/, preset: { forma: "tira",  color: "#4C8B3F", densidad: 12 } },
  { re: /tomate|tomato|cherry/,          preset: { forma: "disco", color: "#D24638", densidad: 12, escala: 1.05 } },
  { re: /\bpina\b|ananas|pineapple/,      preset: { forma: "cuna",  color: "#F0C63C", densidad: 12, escala: 1.05 } },
  { re: /maiz|choclo|elote|corn/,        preset: { forma: "grano", color: "#F2C744", densidad: 22, escala: 0.6 } },
  { re: /albahaca|basil/,                preset: { forma: "hoja",  color: "#3E7A34", densidad: 11, escala: 1.1 } },
  { re: /rucula|arugula|espinaca|kale/,  preset: { forma: "hoja",  color: "#4A7A38", densidad: 13, escala: 1.05 } },
  { re: /oregano|hierba|perejil/,        preset: { forma: "escama", color: "#5C6B3A", densidad: 26, escala: 0.5 } },
  { re: /alcaparra/,                     preset: { forma: "grano", color: "#5E6B45", densidad: 18, escala: 0.6 } },
  { re: /\bpapas?\b|patata/,              preset: { forma: "disco", color: "#E8D6A8", densidad: 12 } },
  { re: /berenjena|zucchini|calabac/,    preset: { forma: "disco", color: "#9AA870", densidad: 12 } },

  // ── Otros ─────────────────────────────────────────────────────────────────
  { re: /huevo|egg/,     preset: { forma: "huevo", color: "#F7EEDC", densidad: 5, escala: 1.6 } },
  { re: /ricotta|crema/, preset: { forma: "grano", color: "#F6F0E4", densidad: 14, escala: 1.2 } },
  { re: /parmesano|pecorino|rallado/, preset: { forma: "escama", color: "#F0E4C0", densidad: 24, escala: 0.5 } },
  // Los quesos de capa NO se dibujan sobre la pizza (`esCapaDeQueso` los excluye),
  // pero SÍ tienen ícono en el desglose: sin entrada acá, "Mozzarella" caía al disco
  // genérico y salía roja al lado de su renglón, como si fuera un embutido.
  { re: /mozzarella|muzzarella|provolone|gouda/, preset: { forma: "grano", color: "#F3DFA2" } },
  { re: /cheddar/,                               preset: { forma: "grano", color: "#E8A33D" } },
  { re: /queso/,                                 preset: { forma: "grano", color: "#F3DFA2" } },
]

/**
 * Devuelve el preset del ingrediente, o `null` si no está en el catálogo.
 *
 * `null` NO es un error: significa "usá lo que te pasaron". Una pizzería con un
 * ingrediente propio sigue viendo algo dibujado, solo que con la forma y el color
 * genéricos de antes.
 */
export function presetDe(nombre: string): PresetIngrediente | null {
  const n = normalizar(nombre)
  for (const { re, preset } of CATALOGO) {
    if (re.test(n)) return preset
  }
  return null
}

/**
 * Los quesos NO se dibujan como piezas sueltas: la capa de queso derretido ya los
 * representa. Pintarlos además como discos los convertía en monedas pálidas flotando
 * ENCIMA del queso — el detalle que más delataba que el dibujo era de juguete.
 */
export function esCapaDeQueso(nombre: string): boolean {
  const n = normalizar(nombre)
  if (/parmesano|pecorino|rallado|ricotta/.test(n)) return false // esos SÍ se ven encima
  return /mozzarella|muzzarella|queso|cheddar|provolone|gouda/.test(n)
}
